import React, { useState, useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { useHistory, useParams } from 'react-router';
import Skeleton from 'react-loading-skeleton';
import cloneDeep from 'lodash/cloneDeep';
import { useTranslation } from 'react-i18next';

import './index.less';

import { alertsSelectorAdd } from '../../../../recoil';
import { ELMA365ToolModel, ELMA365ToolParameterOutput, ELMA365ToolsetModel } from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import { getTranslateFunc, isValidJSON } from '../../../../utils/stringUtil';
import { getToolkitPath, getToolkitsPath, getToolkitToolPath } from '../../../../utils/pathUtils';
import IbTypography from '../../../../components/common/IbTypography';
import IbButton from '../../../../components/common/IbButton';
import IbPageTitle, { IBreadcrumb } from '../../../../components/IbPageTitle';
import IbConfirmModal from '../../../../components/common/IbConfirmModal';
import IbTabs from '../../../../components/common/IbTabs';
import IbTabPane from '../../../../components/common/IbTabPane';
import Settings from '../../../../components/Settings';
import { CheckFieldValueFnMap, useValidationForm } from '../../../../utils/validationUtil';

import {
  CONTENT_CLASS_NAME,
  FOOTER_CLASSNAME,
  MAIN_BUTTONS_CLASSNAME,
  MAIN_CLASS_NAME,
  RESET_BUTTON_CLASSNAME,
  SKELETON_CONTAINER_CLASS_NAME,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
  TABS_CLASS_NAME,
} from './const';
import ToolInfoBar from './ToolInfoBar';
import ParamsTable from './ParamsTable';

const MAX_TOOL_NAME_LENGTH = 64;

enum ToolTabKey {
  Main = 'main',
  JSONSchema = 'json-schema',
  InputParams = 'input-params',
  OutputParams = 'output-params',
}

export type ELMA365ToolForm = {
  name: string;
  inputSchema: string;
  outputSchema: string;
};

const formFieldToTabKeyMap = {
  name: ToolTabKey.Main,
  inputSchema: ToolTabKey.JSONSchema,
  outputSchema: ToolTabKey.JSONSchema,
};

const checkFieldValueFnMap: CheckFieldValueFnMap<ELMA365ToolForm> = {
  name: (value: string) => {
    const trimmedName = (value || '').trim();
    return !!trimmedName && trimmedName.length <= MAX_TOOL_NAME_LENGTH;
  },
  inputSchema: isValidJSON,
  outputSchema: isValidJSON,
};

const ToolPage: React.FC = () => {
  const { t } = useTranslation();
  const translate = getTranslateFunc('Toolkits: ELMA365: Tool:');

  const { push } = useHistory();

  const { folderId, toolsetId, toolId } = useParams<{ folderId: string; toolsetId: string; toolId: string }>();
  const tabKey = useParams<{ tabKey?: string }>().tabKey?.toLowerCase() ?? ToolTabKey.Main;

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [toolset, setToolset] = useState<ELMA365ToolsetModel>();
  const [tool, setTool] = useState<ELMA365ToolModel>();
  const [editTool, setEditTool] = useState<ELMA365ToolModel>();
  const [editInputSchema, setEditInputSchema] = useState('');
  const [editOutputSchema, setEditOutputSchema] = useState('');

  const initialFormValues: ELMA365ToolForm = {
    name: editTool?.attributes.name.value || '',
    inputSchema: editTool ? JSON.stringify(editTool.interface.input.json_schema, undefined, 2) : '',
    outputSchema: editTool ? JSON.stringify(editTool.interface.output.json_schema, undefined, 2) : '',
  };

  const validationForm = useValidationForm<ELMA365ToolForm>(initialFormValues, checkFieldValueFnMap);

  const { validationState, validate, reset } = validationForm;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetConfirmLoading, setResetConfirmLoading] = useState(false);

  const breadcrumbs: IBreadcrumb[] = [
    { title: t(`Toolkits: ELMA365: Toolset: Toolsets`), path: getToolkitsPath(folderId) },
    { title: toolset?.name ?? '', path: getToolkitPath(folderId, toolsetId) },
  ];

  const setTabKey = (value: string) => {
    push(getToolkitToolPath(folderId, toolsetId, toolId, value));
  };

  const loadToolset = async () => {
    if (!toolsetId) return;

    try {
      setLoading(true);

      const toolsetResponse = await defaultApi.getOneELMA365ToolkitToolset(toolsetId);
      const toolset = toolsetResponse.data;
      setToolset(toolset);
    } catch (err) {
      addAlert({ content: translate('Failed to load toolset'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadToolset().finally();
  }, [toolsetId]);

  const loadTool = async () => {
    if (!toolId) return;

    try {
      setLoading(true);

      const toolResponse = await defaultApi.getOneELMA365ToolkitTool(toolId);
      const tool = toolResponse.data;
      setTool(tool);
      setEditTool(cloneDeep(tool));

      const inputSchema = tool ? JSON.stringify(tool.interface.input.json_schema, undefined, 2) : '';
      const outputSchema = tool ? JSON.stringify(tool.interface.output.json_schema, undefined, 2) : '';

      setEditInputSchema(inputSchema);
      setEditOutputSchema(outputSchema);
      reset({
        name: tool?.attributes.name.value || '',
        inputSchema: inputSchema,
        outputSchema: outputSchema,
      });
    } catch (err) {
      addAlert({ content: translate('Failed to load tool'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadTool().finally();
  }, [toolId]);

  const onTabChange = (key: string) => setTabKey(key as ToolTabKey);

  const onSave = async () => {
    if (!editTool) {
      return;
    }

    if (!validate()) {
      const fieldsOrder = Object.keys(formFieldToTabKeyMap) as Array<keyof typeof formFieldToTabKeyMap>;

      // Collect fields that are invalid
      type FieldState = { isValid?: boolean };
      const validationStateMap = (validationState as unknown) as Record<string, FieldState>;
      const invalidFields = fieldsOrder.filter((field) => {
        const fieldState = validationStateMap[field];
        return fieldState && fieldState.isValid === false;
      });

      if (!invalidFields.length) return;

      // If the currently active tab already contains at least one invalid field,
      // keep the user on that tab so they can fix fields there without jumping.
      const currentTabHasError = invalidFields.some((f) => formFieldToTabKeyMap[f] === tabKey);
      if (currentTabHasError) return;

      // Otherwise open the tab for the first invalid field
      setTabKey(formFieldToTabKeyMap[invalidFields[0]]);

      return;
    }

    setSaving(true);

    try {
      const inputSchemaObject = JSON.parse(editInputSchema);
      const outputSchemaObject = JSON.parse(editOutputSchema);

      editTool.interface.input.json_schema = inputSchemaObject;
      editTool.interface.output.json_schema = outputSchemaObject;

      await defaultApi.updateELMA365ToolkitTool(toolId, { ...editTool });
    } catch (err) {
      addAlert({ content: translate('Failed to save tool'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }

    await loadTool();
  };

  const onCancel = () => {
    push(getToolkitPath(folderId, toolsetId));
  };

  const onReset = () => {
    setResetModalVisible(true);
  };

  const onResetConfirmButtonClick = async () => {
    setResetConfirmLoading(true);

    await loadTool();

    setResetConfirmLoading(false);
    setResetModalVisible(false);
  };

  const onResetCancelButtonClick = () => {
    setResetModalVisible(false);
  };

  const onToolChange = (tool: ELMA365ToolModel) => {
    setEditTool(tool);
  };

  const onSchemaChange = (inputSchema: string, outputSchema: string) => {
    setEditInputSchema(inputSchema);
    setEditOutputSchema(outputSchema);
  };

  const getUpdatedParameters = (newParameter: ELMA365ToolParameterOutput, parameters: ELMA365ToolParameterOutput[]) => {
    const idx = parameters.findIndex((p) => p.code === newParameter.code);

    if (idx !== -1) {
      return parameters.map((p, index) => {
        if (index === idx) {
          return { ...p, ...newParameter };
        }
        return p;
      });
    } else {
      return [...parameters, newParameter];
    }
  };

  const onInputParameterChange = (parameter: ELMA365ToolParameterOutput) => {
    if (!editTool) return;

    const newParameters = getUpdatedParameters(parameter, editTool.interface.input.parameters);

    const newTool = {
      ...editTool,
      interface: {
        ...editTool.interface,
        input: {
          ...editTool.interface.input,
          parameters: newParameters,
        },
      },
    };

    setEditTool(newTool);
  };

  const onOutputParameterChange = (parameter: ELMA365ToolParameterOutput) => {
    if (!editTool) return;

    const newParameters = getUpdatedParameters(parameter, editTool.interface.output.parameters);

    const newTool = {
      ...editTool,
      interface: {
        ...editTool.interface,
        output: {
          ...editTool.interface.output,
          parameters: newParameters,
        },
      },
    };

    setEditTool(newTool);
  };

  const buttonsDisabled = saving || loading;

  const renderFooter = () => {
    return (
      <>
        <div className={MAIN_BUTTONS_CLASSNAME}>
          <IbButton disabled={buttonsDisabled} loading={saving} onClick={onSave}>
            {translate('Footer: Save')}
          </IbButton>
          <IbButton disabled={buttonsDisabled} type="secondary" onClick={onCancel}>
            {translate('Footer: Cancel')}
          </IbButton>
        </div>
        <div className={RESET_BUTTON_CLASSNAME}>
          <IbButton disabled={buttonsDisabled} status="error" type="secondary" onClick={onReset}>
            {translate('Footer: Reset')}
          </IbButton>
        </div>
      </>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle breadcrumbs={breadcrumbs} loading={loading} title={tool?.attributes.name.value} />
      <div className={CONTENT_CLASS_NAME}>
        {editTool ? (
          <>
            <ToolInfoBar tool={editTool} />
            <IbTabs activeKey={tabKey} className={TABS_CLASS_NAME} onChange={onTabChange}>
              <IbTabPane key={ToolTabKey.Main} tab={translate('Main')} tabKey={ToolTabKey.Main}>
                <Settings.ELMA365ToolMain tool={editTool} validationForm={validationForm} onChange={onToolChange} />
              </IbTabPane>
              <IbTabPane key={ToolTabKey.JSONSchema} tab={translate('JSON Schema')} tabKey={ToolTabKey.JSONSchema}>
                <Settings.ELMA365ToolJSONSchema
                  inputSchema={editInputSchema}
                  outputSchema={editOutputSchema}
                  toolId={toolId}
                  validationForm={validationForm}
                  onChange={onSchemaChange}
                />
              </IbTabPane>
              <IbTabPane key={ToolTabKey.InputParams} tab={translate('Input params')} tabKey={ToolTabKey.InputParams}>
                <ParamsTable
                  loading={loading}
                  parameters={editTool.interface.input.parameters}
                  onParameterChange={onInputParameterChange}
                />
              </IbTabPane>
              <IbTabPane
                key={ToolTabKey.OutputParams}
                tab={translate('Output params')}
                tabKey={ToolTabKey.OutputParams}
              >
                <ParamsTable
                  loading={loading}
                  parameters={editTool.interface.output.parameters}
                  onParameterChange={onOutputParameterChange}
                />
              </IbTabPane>
            </IbTabs>
          </>
        ) : (
          <div className={SKELETON_CONTAINER_CLASS_NAME}>
            <Skeleton height={SKELETON_HEIGHT} width="100%" />
            <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
          </div>
        )}
      </div>
      <div className={FOOTER_CLASSNAME}>{renderFooter()}</div>
      <IbConfirmModal
        footer={
          <>
            <IbButton key="reset-confirm" status="error" onClick={onResetConfirmButtonClick}>
              {translate('Reset confirm modal: Reset')}
            </IbButton>
            <IbButton key="reset-cancel" type="secondary" onClick={onResetCancelButtonClick}>
              {translate('Reset confirm modal: Cancel')}
            </IbButton>
          </>
        }
        loading={resetConfirmLoading}
        title={translate('Reset confirm modal: Title')}
        visible={resetModalVisible}
        onCancel={onResetCancelButtonClick}
      >
        <IbTypography.Paragraph type="secondary">{translate('Reset confirm modal: Content')}</IbTypography.Paragraph>
      </IbConfirmModal>
    </div>
  );
};

export default ToolPage;
